import { reactive } from "vue";

export default function ($sdk) {
  const service = reactive({
    items: [],
    hash: {},
    hex: {},
    loading: false,
    loaded: false,
    _fullyLoaded: {},
  });

  let indexPromise = null;
  const definitionPromises = {};

  ///////////////////////////////////////////////////

  /**
   * Merge properties from source onto target in-place.
   * This preserves the reactive proxy reference so Vue
   * picks up the changes automatically.
   */
  function mergeInPlace(target, source) {
    if (!target || !source) return;
    for (const key of Object.keys(source)) {
      target[key] = source[key];
    }
  }

  /**
   * Build hash (keyed by .key) and hex (keyed by .hex)
   * lookup objects from the items array.
   */
  function buildLookups(items) {
    const hash = {};
    const hex = {};
    items.forEach(function (item) {
      if (item.key) hash[item.key] = item;
      if (item.hex) hex[item.hex] = item;
    });
    return { hash, hex };
  }

  ///////////////////////////////////////////////////
  // Index loading
  ///////////////////////////////////////////////////

  async function loadIndex(forceReload) {
    if (indexPromise) {
      return indexPromise;
    }

    if (service.loaded && !forceReload) {
      return;
    }

    indexPromise = _doLoadIndex(forceReload);
    return indexPromise;
  }

  async function _doLoadIndex(forceReload) {
    const user = $sdk.auth.getCurrentUser();
    if (!user) {
      clear();
      return;
    }

    service.loading = true;

    try {
      const { data } = await $sdk.api.get("/glossary/index", { cache: false });
      const items = data || [];

      const { hash, hex } = buildLookups(items);

      // Reset full-load tracking on index reload
      service._fullyLoaded = {};
      service.items = items;
      service.hash = hash;
      service.hex = hex;
      service.loaded = true;
    } catch (err) {
      console.error("GlossaryService: failed to load glossary index", err);
    } finally {
      service.loading = false;
      indexPromise = null;
    }
  }

  ///////////////////////////////////////////////////
  // On-demand definition loading
  ///////////////////////////////////////////////////

  async function loadDefinition(key, forceReload) {
    if (service._fullyLoaded[key] && !forceReload) {
      return service.hash[key];
    }

    // Deduplicate inflight requests per key
    if (definitionPromises[key]) {
      return definitionPromises[key];
    }

    definitionPromises[key] = _doLoadDefinition(key, forceReload);
    return definitionPromises[key];
  }

  async function _doLoadDefinition(key, forceReload) {
    try {
      const { data } = await $sdk.api.get(`/glossary/index/${key}`, {
        cache: false,
      });

      if (!data) return;

      // Merge into existing hash entry in-place for reactivity
      if (service.hash[key]) {
        mergeInPlace(service.hash[key], data);
      } else {
        service.hash[key] = data;
      }

      // Also update the matching item in the items array
      const itemIndex = service.items.findIndex(function (item) {
        return item.key === key;
      });
      if (itemIndex !== -1) {
        mergeInPlace(service.items[itemIndex], data);
      }

      service._fullyLoaded[key] = true;
      return service.hash[key];
    } catch (err) {
      console.error(
        `GlossaryService: failed to load definition for '${key}'`,
        err,
      );
    } finally {
      delete definitionPromises[key];
    }
  }

  ///////////////////////////////////////////////////
  // Public API
  ///////////////////////////////////////////////////

  /**
   * Ensure glossary data is loaded.
   *
   * ensure()                     - load index only (fast startup)
   * ensure('profile')            - ensure single definition is fully loaded
   * ensure(['profile', 'event']) - ensure multiple definitions are fully loaded
   */
  service.ensure = async function (keyOrKeys) {
    // No arguments — just load the index
    if (!keyOrKeys) {
      return loadIndex(false);
    }

    // Make sure the index is loaded first
    await loadIndex(false);

    // Single key
    if (typeof keyOrKeys === "string") {
      return loadDefinition(keyOrKeys, false);
    }

    // Array of keys
    if (Array.isArray(keyOrKeys)) {
      return Promise.all(
        keyOrKeys.map(function (key) {
          return loadDefinition(key, false);
        }),
      );
    }
  };

  /**
   * Reload glossary data.
   *
   * reload()          - reload entire index, clear all full-load state
   * reload('profile') - reload single definition
   */
  service.reload = async function (keyOrKeys) {
    // No arguments — reload entire index
    if (!keyOrKeys) {
      return loadIndex(true);
    }

    // Make sure the index is loaded first
    await loadIndex(false);

    // Single key
    if (typeof keyOrKeys === "string") {
      return loadDefinition(keyOrKeys, true);
    }

    // Array of keys
    if (Array.isArray(keyOrKeys)) {
      return Promise.all(
        keyOrKeys.map(function (key) {
          return loadDefinition(key, true);
        }),
      );
    }
  };

  service.definitionName = function (key, plural) {
    const definition = service.hash[key];
    if (definition) {
      return plural ? definition.plural : definition.title;
    }
    return key;
  };

  service.clear = clear;

  ///////////////////////////////////////////////////

  function clear() {
    service.items = [];
    service.hash = {};
    service.hex = {};
    service.loaded = false;
    service.loading = false;
    service._fullyLoaded = {};
    indexPromise = null;
    for (const key in definitionPromises) {
      delete definitionPromises[key];
    }
  }

  let _lastOrgId = null;

  $sdk.auth.addEventListener("change", function (user) {
    if (user) {
      const orgId = user?.session?.organisation?._id;
      if (_lastOrgId && orgId !== _lastOrgId) {
        clear();
      }
      _lastOrgId = orgId || null;
      loadIndex(false);
    } else {
      _lastOrgId = null;
      clear();
    }
  });

  const currentUser = $sdk.auth.getCurrentUser();
  if (currentUser) {
    _lastOrgId = currentUser?.session?.organisation?._id || null;
    loadIndex(false);
  }

  return service;
}
